// YERİ: src/app/domains/site/sections/slider.section.ts
//
// Slayt gösterisi — görsel + başlık + metin + buton taşıyan yatay kaydırıcı.
//
// ── KAYDIRMA NEDEN ELLE ÇİZİLİYOR ───────────────────────────────────────────
// Vitrindeki `mkt-slider` deneyimi: `scroll-snap-type: mandatory` açıkken
// `scrollTo({behavior:'smooth'})` rayı çoğu zaman hiç kımıldatmıyor, bazen de
// başa atıyor. Burada snap 'proximity' (site-decor.css), programatik geçiş ise
// requestAnimationFrame ile kendi eğrisiyle çiziliyor ve süre boyunca snap
// `.is-seeking` ile kapatılıyor. RTL'de de aynı kod çalışır: hedef her zaman
// `slaytIndex * rayGenisligi` değil, ilgili çocuğun `offsetLeft`i üzerinden
// hesaplanır — RTL'de offsetLeft negatif/ters olabildiği için tarayıcının
// verdiği değere güvenilir, elle çarpım yapılmaz.
//
// Otomatik geçiş: kullanıcı rayla etkileşince (dokunma/fare/klavye) DURUR ve
// bir daha başlamaz. Okumaya çalışırken slaydın altından kayması, otomatik
// geçişin en sık şikâyet edilen yanı.

import {
  Component, ElementRef, OnDestroy, computed, effect, input, signal, viewChild,
} from '@angular/core';
import { MIcon } from '@/app/shared/ui/m-icon';
import { SiteConfig, cfg } from './section.model';

interface Slide {
  image?: string;
  title?: string;
  text?: string;
  ctaText?: string;
  ctaUrl?: string;
}

@Component({
  selector: 'sec-slider',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="slider" class="relative overflow-hidden bg-neutral-950">
      <div #rail class="ws-rail" (pointerdown)="stopAuto()" (keydown)="stopAuto()" (wheel)="stopAuto()">
        @for (s of slides(); track $index) {
          <div class="relative h-[clamp(320px,52vw,560px)] w-full">
            @if (s.image) {
              <img [src]="s.image" [alt]="s.title || ''" class="absolute inset-0 h-full w-full object-cover" />
              <div class="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10"></div>
            } @else {
              <!-- Görsel yoksa marka renginde zemin: boş siyah bir kutu yerine
                   sitenin kimliğiyle uyumlu bir slayt. -->
              <div class="absolute inset-0" style="background:linear-gradient(120deg,var(--site-primary),var(--site-primary-dark))"></div>
              <div class="ws-pattern-light absolute inset-0"></div>
            }

            <div class="relative mx-auto flex h-full max-w-7xl flex-col justify-center px-6 lg:px-8">
              <div class="max-w-2xl">
                @if (s.title) {
                  <h2 class="font-[family-name:var(--site-head-font)] text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">{{ s.title }}</h2>
                }
                @if (s.text) {
                  <p class="mt-4 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">{{ s.text }}</p>
                }
                @if (s.ctaText) {
                  <a [href]="s.ctaUrl || '#'"
                     class="ws-btn mt-8 inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
                     [class]="fxClass()"
                     style="background-color:var(--site-accent)">
                    {{ s.ctaText }}
                  </a>
                }
              </div>
            </div>
          </div>
        }
      </div>

      @if (slides().length > 1) {
        <!-- Oklar: dokunmatikte gizli, orada kaydırma zaten doğal. -->
        <button type="button" class="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white backdrop-blur transition hover:bg-white/30 sm:block"
                [attr.aria-label]="prevLabel()" (click)="go(-1)">
          <m-icon name="chevron-left" size="sm" />
        </button>
        <button type="button" class="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white backdrop-blur transition hover:bg-white/30 sm:block"
                [attr.aria-label]="nextLabel()" (click)="go(1)">
          <m-icon name="chevron-right" size="sm" />
        </button>

        <div class="absolute inset-x-0 bottom-4 flex justify-center gap-2">
          @for (s of slides(); track $index) {
            <button type="button" class="h-2 rounded-full transition-all"
                    [class]="$index === index() ? 'w-6 bg-white' : 'w-2 bg-white/45 hover:bg-white/70'"
                    [attr.aria-label]="slideLabel() + ' ' + ($index + 1)"
                    (click)="goTo($index)"></button>
          }
        </div>
      }
    </section>
  `,
})
export class SliderSection implements OnDestroy {
  config = input<SiteConfig>({});
  /**
   * Önizleme (şablon kartı / editör). Otomatik geçiş burada KAPALI: on altı
   * kartın slaytları aynı anda dönerse sayfa huzursuz olur, editörde de
   * düzenlenen slayt altından kayar.
   */
  preview = input<boolean>(false);

  private rail = viewChild<ElementRef<HTMLElement>>('rail');

  protected index = signal(0);
  protected slides = computed<Slide[]>(() => {
    const raw = this.config()['slides'];
    return Array.isArray(raw) && raw.length ? raw.filter((s: Slide) => s?.title || s?.image) : [
      { title: 'Birinci Slayt Başlığı', text: 'Slayta kısa bir açıklama yazın.', ctaText: 'Daha Fazla', ctaUrl: '#' },
      { title: 'İkinci Slayt Başlığı', text: 'Görsel yüklerseniz arka planda gösterilir.', ctaText: 'İletişim', ctaUrl: '#contact' },
    ];
  });

  protected fxClass = computed(() => {
    const fx = String(cfg(this.config(), 'buttonEffect', ''));
    return fx ? 'ws-fx-' + fx : '';
  });

  protected prevLabel = computed(() => cfg(this.config(), 'prevLabel', 'Önceki'));
  protected nextLabel = computed(() => cfg(this.config(), 'nextLabel', 'Sonraki'));
  protected slideLabel = computed(() => cfg(this.config(), 'slideLabel', 'Slayt'));

  /** Otomatik geçiş süresi (sn). 0/boş → kapalı. */
  private autoSeconds = computed(() => Number(cfg(this.config(), 'autoplaySeconds', 0)) || 0);

  private timer: ReturnType<typeof setInterval> | null = null;
  private raf = 0;
  private userStopped = false;

  constructor() {
    effect(() => {
      const secs = this.autoSeconds();
      this.clearTimer();
      // SSR'de setInterval kurmak sunucu tarafında boşuna iş; hidrasyondan
      // sonra istemcide zaten yeniden kurulur.
      if (secs > 0 && !this.userStopped && !this.preview() && typeof window !== 'undefined') {
        this.timer = setInterval(() => this.go(1), secs * 1000);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTimer();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /** Kullanıcı rayla etkileşti — otomatik geçiş kalıcı olarak dursun. */
  protected stopAuto(): void {
    this.userStopped = true;
    this.clearTimer();
  }

  protected go(delta: number): void {
    const n = this.slides().length;
    if (n < 2) return;
    this.goTo((this.index() + delta + n) % n);
  }

  protected goTo(i: number): void {
    const el = this.rail()?.nativeElement;
    if (!el) return;
    const child = el.children[i] as HTMLElement | undefined;
    if (!child) return;

    this.index.set(i);
    // Hedef, çocuğun kendi konumundan okunur: RTL'de ray ters yönde ilerlediği
    // için "i * genişlik" yanlış sonuç verirdi.
    this.animateTo(el, child.offsetLeft - el.offsetLeft);
  }

  /**
   * Rayı hedefe kendi eğrisiyle kaydırır.
   * `behavior:'smooth'` KULLANILMIYOR — snap ile birlikte çalışmıyor.
   */
  private animateTo(el: HTMLElement, target: number): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    el.classList.add('is-seeking');

    const start = el.scrollLeft;
    const delta = target - start;
    const dur = 420;
    let t0 = 0;

    const step = (ts: number) => {
      if (!t0) t0 = ts;
      const p = Math.min(1, (ts - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);       // easeOutCubic
      el.scrollLeft = start + delta * eased;
      if (p < 1) {
        this.raf = requestAnimationFrame(step);
      } else {
        this.raf = 0;
        el.classList.remove('is-seeking');
      }
    };
    this.raf = requestAnimationFrame(step);
  }

  private clearTimer(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }
}
